import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

const AppContext = createContext();

// Crear el proveedor del contexto
export const AppProvider = ({ children }) => {
  const [ctxApp, setCtxApp] = useState(null);
  const [ctxUser, setCtxUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [sincroExecucionsData, setSincroExecucionsData] = useState([]); // Nou estat per a les dades de sincronització
  const [conceptesList, setConceptesList] = useState([]);
  const [graphMode, setGraphMode] = useState('CLASSIC'); // 'DUAL' o 'CLASSIC'
  const [conceptesTree, setConceptesTree] = useState([]);
  const [entitatsTransversals, setEntitatsTransversals] = useState([]);
  const [conceptesNodes, setConceptesNodes] = useState([]);
  const [conceptesNodesMap, setConceptesNodesMap] = useState(new Map());

  const loadGraphData = (jwtToken, userapp) => {
    if (!jwtToken || !userapp) return;

    // Obtenir mode
    axios.get(`${config.apiBaseUrl}/v1/conceptes-graph/mode`, {
      headers: { Authorization: jwtToken }
    })
    .then((res) => {
      const mode = res.data?.mode || 'CLASSIC';
      setGraphMode(mode);
    })
    .catch((err) => {
      console.warn('Fallback a mode CLASSIC:', err);
      setGraphMode('CLASSIC');
    });

    // Obtenir arbre
    axios.get(`${config.apiBaseUrl}/v1/conceptes-graph/tree?userapp=${userapp}`, {
      headers: { Authorization: jwtToken }
    })
    .then((res) => {
      setConceptesTree(res.data || []);
    })
    .catch((err) => {
      console.warn('Error en carregar arbre de conceptes:', err);
    });

    // Obtenir entitats transversals
    axios.get(`${config.apiBaseUrl}/v1/conceptes-graph/entitats?userapp=${userapp}`, {
      headers: { Authorization: jwtToken }
    })
    .then((res) => {
      setEntitatsTransversals(res.data || []);
    })
    .catch((err) => {
      console.warn('Error en carregar entitats transversals:', err);
    });

    // Obtenir catàleg pla de conceptes node
    axios.get(`${config.apiBaseUrl}/v1/conceptes-graph/nodes?userapp=${userapp}`, {
      headers: { Authorization: jwtToken }
    })
    .then((res) => {
      const list = res.data || [];
      const map = new Map();
      list.forEach((n) => {
        if (n && n.id != null) {
          map.set(Number(n.id), n);
        }
      });
      setConceptesNodes(list);
      setConceptesNodesMap(map);
    })
    .catch((err) => {
      console.warn('Error en carregar catàleg de nodes:', err);
    });
  };

  useEffect(() => {
    const jwtToken = ctxUser?.fdvToken?.jwtToken;
    const userapp = ctxApp?.userapp;

    if (jwtToken && userapp) {
      axios.get(`${config.apiBaseUrl}/v1/conceptes/byuserapp?userapp=${userapp}`, {
        headers: { Authorization: jwtToken }
      })
      .then((response) => {
        const sorted = (response.data || []).sort((a, b) => a.nom.localeCompare(b.nom));
        setConceptesList(sorted);
      })
      .catch((err) => {
        console.error('Error al cargar conceptos en contexto:', err);
      });

      loadGraphData(jwtToken, userapp);
    } else {
      setConceptesList([]);
      setConceptesTree([]);
      setEntitatsTransversals([]);
      setConceptesNodes([]);
      setConceptesNodesMap(new Map());
      setGraphMode('CLASSIC');
    }
  }, [ctxUser?.fdvToken?.jwtToken, ctxApp?.userapp]);

  const reloadConceptesGraph = () => {
    const jwtToken = ctxUser?.fdvToken?.jwtToken;
    const userapp = ctxApp?.userapp;
    loadGraphData(jwtToken, userapp);
  };

  const getNodeById = (id) => {
    if (id == null) return null;
    return conceptesNodesMap.get(Number(id)) || null;
  };

  const value = {
    ctxApp, setCtxApp, 
    ctxUser, setCtxUser,
    authLoading, setAuthLoading,
    sincroExecucionsData, setSincroExecucionsData,
    conceptesList, setConceptesList,
    graphMode, setGraphMode,
    conceptesTree, setConceptesTree,
    entitatsTransversals, setEntitatsTransversals,
    conceptesNodes, setConceptesNodes,
    conceptesNodesMap, setConceptesNodesMap,
    getNodeById,
    reloadConceptesGraph,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => { // Hook personalitzat per consumir el context més fàcilment
  const context = useContext(AppContext);
  if (context === undefined) {
      throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
